import { useCallback } from 'react';
import { useGetDevicesQuery } from 'src/api/device';
import { useGetInteractionsQuery } from 'src/api/interactions';
import { useGetMyBrandsQuery } from 'src/api/branding';
import { useAddCampaignSlotMutation } from 'src/api/campaigns/hooks';
import { useGlobalModals } from 'src/contexts/global-modals';
import {
  validateSelect,
  validateEmpty,
  validateDateRange,
} from 'src/helpers/validators';

interface UseAddCampaignSlotModalProps {
  campaignId: number;
  onClose: () => void;
}

interface FormValues {
  name: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  deviceId: string;
  interactionId: string;
  brandingId: string;
}

const validation = {
  name: (value: string) => validateEmpty(value, 'Name is required'),
  deviceId: (value: string) => validateEmpty(value, 'Device is required'),
  interactionId: (value: string) =>
    validateSelect(value, 'Interaction is required'),
  brandingId: (value: string) => validateSelect(value, 'Branding is required'),
  dateFrom: (value: Date | null) =>
    validateEmpty(value ? value.toString() : '', 'Start date is required'),
  dateTo: (value: Date | null, allValues: any) =>
    validateDateRange(
      value,
      allValues.dateFrom,
      'End date is required',
      'End date must be after start date'
    ),
};

export const useAddCampaignSlotModal = ({
  campaignId,
  onClose,
}: UseAddCampaignSlotModalProps) => {
  const { data: devices } = useGetDevicesQuery();
  const { data: interactions } = useGetInteractionsQuery();
  const { data: brandings } = useGetMyBrandsQuery();
  const { addCampaignSlotMutation, isPending } = useAddCampaignSlotMutation();
  const { openLoadingModal, closeLoadingModal } = useGlobalModals();

  const handleSubmit = useCallback(
    async (data: FormValues) => {
      if (!data.dateFrom || !data.dateTo) return;

      openLoadingModal(
        'Creating slot',
        'Please wait while we create the campaign slot...'
      );

      const dateFrom = new Date(data.dateFrom).toISOString();
      const dateTo = new Date(data.dateTo).toISOString();

      try {
        await addCampaignSlotMutation({
          campaign_id: campaignId,
          name: data.name,
          date_from: dateFrom,
          date_to: dateTo,
          device_id: parseInt(data.deviceId),
          interaction_id: parseInt(data.interactionId),
          branding_id: parseInt(data.brandingId),
        });
        onClose();
      } finally {
        closeLoadingModal();
      }
    },
    [
      campaignId,
      addCampaignSlotMutation,
      openLoadingModal,
      closeLoadingModal,
      onClose,
    ]
  );

  const handleClose = useCallback(() => {
    if (!isPending) {
      onClose();
    }
  }, [isPending, onClose]);

  return {
    devices: devices || [],
    interactions: interactions || [],
    brandings: brandings || [],
    isPending,
    handleSubmit,
    handleClose,
    validation,
  };
};
